import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, BarChart3, Download, Search, Filter, RefreshCw, AlertTriangle, ListChecks, ChevronDown, ChevronRight,
  MapPin, Printer, Plus, Package, ArrowUp, ArrowDown, ArrowUpDown, ArrowUpRight, FileText, Truck, Scale,
} from "lucide-react";
import SafetyShell from "../safety/SafetyShell";
import { pushRecent } from "../safety/recents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_META = {
  not_started: { label: "Pending", cls: "bg-red-100 text-red-700 border-red-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  complete: { label: "Complete", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  over: { label: "Over-produced", cls: "bg-purple-100 text-purple-700 border-purple-200" },
};

const DISPATCH_META = {
  fully: { label: "Dispatched", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  partial: { label: "Partially Dispatched", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  none: { label: "Not Dispatched", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

export default function PendingProductionTracker({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ lines: [], pos: [], overall: null });

  // Filters
  const initialIds = (searchParams.get("po_ids") || "").split(",").filter(Boolean);
  const [selectedPoIds, setSelectedPoIds] = useState(initialIds);
  const [includePending, setIncludePending] = useState(
    (searchParams.get("include_pending") ?? "true") === "true"
  );
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(searchParams.get("customer") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [dispatchFilter, setDispatchFilter] = useState(searchParams.get("dispatch") || "all");
  const [groupBy, setGroupBy] = useState(searchParams.get("group_by") || "po");
  const [sortBy, setSortBy] = useState("pending_desc");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedShiptoMats, setExpandedShiptoMats] = useState({}); // `${shipto}|${code}` -> bool
  const [expandedLines, setExpandedLines] = useState({}); // `${po_id}|${code}` -> bool
  const [lineReports, setLineReports] = useState({}); // same key -> null (loading) | array

  // Ship-To data (only when groupBy === 'shipto')
  const [shiptoGroups, setShiptoGroups] = useState(null); // null | array
  const [shiptoLoading, setShiptoLoading] = useState(false);
  // Per-ship-to PO exclusion (default: all POs included). { [shiptoCanonical]: { [poId]: true } }
  const [excludedPoByShipto, setExcludedPoByShipto] = useState({});
  // Quantity column mode for ship-to view: "pending" | "ordered" | "both"
  const [qtyMode, setQtyMode] = useState("pending");
  // Catalog alias-health: count of product codes not yet linked (pending may be understated)
  const [aliasMismatchCount, setAliasMismatchCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/catalog/alias-health`, auth())
      .then((r) => setAliasMismatchCount(r.data?.total || 0))
      .catch(() => { /* no catalog.view or error — hide banner */ });
  }, []);

  useEffect(() => {
    if (groupBy !== "shipto") return;
    setShiptoLoading(true);
    axios.get(`${API}/production/pending-by-shipto?include_pending_approval=${includePending}`, auth())
      .then((r) => setShiptoGroups(r.data.groups || []))
      .catch(() => { toast.error("Failed to load ship-to grouping"); setShiptoGroups([]); })
      .finally(() => setShiptoLoading(false));
  }, [groupBy, includePending]);

  // PO picker dialog
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  const [openPos, setOpenPos] = useState([]);
  const [poPickerSelected, setPoPickerSelected] = useState({});
  const [poSearch, setPoSearch] = useState("");

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [selectedPoIds.join(","), includePending, dispatchFilter]);

  // Sync filters to URL
  useEffect(() => {
    const next = {};
    if (selectedPoIds.length) next.po_ids = selectedPoIds.join(",");
    if (!includePending) next.include_pending = "false";
    if (search) next.q = search;
    if (customerFilter !== "all") next.customer = customerFilter;
    if (statusFilter !== "all") next.status = statusFilter;
    if (dispatchFilter !== "all") next.dispatch = dispatchFilter;
    if (groupBy !== "po") next.group_by = groupBy;
    setSearchParams(next, { replace: true });
  }, [selectedPoIds, includePending, search, customerFilter, statusFilter, dispatchFilter, groupBy, setSearchParams]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (selectedPoIds.length) qs.set("po_ids", selectedPoIds.join(","));
      qs.set("include_pending_approval", String(includePending));
      qs.set("dispatch_status", dispatchFilter);
      const res = await axios.get(`${API}/production/pending-summary?${qs.toString()}`, auth());
      setSummary(res.data);
      // Expand all groups by default
      const expanded = {};
      (res.data.pos || []).forEach(p => { expanded[p.po_id] = true; });
      setExpandedGroups(expanded);
    } catch (e) {
      toast.error("Failed to load pending-production summary");
    } finally { setLoading(false); }
  };

  const openPoPicker = async () => {
    try {
      const res = await axios.get(`${API}/production/open-pos`, auth());
      const sorted = [...res.data].sort((a, b) => (a.po_number || "").localeCompare(b.po_number || ""));
      setOpenPos(sorted);
      const pre = {};
      selectedPoIds.forEach(id => { pre[id] = true; });
      setPoPickerSelected(pre);
      setPoPickerOpen(true);
    } catch (e) { toast.error("Failed to load POs"); }
  };
  const filteredOpenPos = openPos.filter(p => {
    if (!poSearch) return true;
    const q = poSearch.toLowerCase();
    return (p.po_number || "").toLowerCase().includes(q) ||
           (p.customer_name || "").toLowerCase().includes(q) ||
           (p.ship_to_name || "").toLowerCase().includes(q);
  });
  const confirmPoPicker = () => {
    const ids = Object.keys(poPickerSelected).filter(k => poPickerSelected[k]);
    setSelectedPoIds(ids);
    setPoPickerOpen(false);
  };
  const selectAllOpenPos = () => {
    const all = {};
    openPos.forEach(p => { all[p.id] = true; });
    setPoPickerSelected(all);
  };
  const clearPickerSelection = () => setPoPickerSelected({});

  // Filter / sort lines
  const customerOptions = useMemo(() => {
    const s = new Set();
    (summary.pos || []).forEach(p => { if (p.customer_name) s.add(p.customer_name); });
    return Array.from(s).sort();
  }, [summary]);

  const filteredLines = useMemo(() => {
    const q = search.trim().toLowerCase();
    let ls = (summary.lines || []).filter(ln => {
      if (customerFilter !== "all" && ln.customer_name !== customerFilter) return false;
      if (statusFilter !== "all" && ln.status !== statusFilter) return false;
      if (!q) return true;
      return (ln.po_number || "").toLowerCase().includes(q) ||
             (ln.product_code || "").toLowerCase().includes(q) ||
             (ln.description || "").toLowerCase().includes(q);
    });
    const cmp = {
      pending_desc: (a, b) => b.pending_qty - a.pending_qty,
      pending_asc: (a, b) => a.pending_qty - b.pending_qty,
      po_asc: (a, b) => (a.po_number || "").localeCompare(b.po_number || ""),
      po_desc: (a, b) => (b.po_number || "").localeCompare(a.po_number || ""),
      product_asc: (a, b) => (a.product_code || "").localeCompare(b.product_code || ""),
      product_desc: (a, b) => (b.product_code || "").localeCompare(a.product_code || ""),
      customer_asc: (a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""),
      customer_desc: (a, b) => (b.customer_name || "").localeCompare(a.customer_name || ""),
      description_asc: (a, b) => (a.description || "").localeCompare(b.description || ""),
      description_desc: (a, b) => (b.description || "").localeCompare(a.description || ""),
      order_asc: (a, b) => a.order_qty - b.order_qty,
      order_desc: (a, b) => b.order_qty - a.order_qty,
      accepted_asc: (a, b) => a.accepted_qty - b.accepted_qty,
      accepted_desc: (a, b) => b.accepted_qty - a.accepted_qty,
      rework_asc: (a, b) => a.rework_qty - b.rework_qty,
      rework_desc: (a, b) => b.rework_qty - a.rework_qty,
      percent_asc: (a, b) => a.percent - b.percent,
      percent_desc: (a, b) => b.percent - a.percent,
      status_asc: (a, b) => (a.status || "").localeCompare(b.status || ""),
      status_desc: (a, b) => (b.status || "").localeCompare(a.status || ""),
    }[sortBy] || ((a, b) => b.pending_qty - a.pending_qty);
    return [...ls].sort(cmp);
  }, [summary, search, customerFilter, statusFilter, sortBy]);

  // Group — group order respects the active Sort selection (was hardcoded asc).
  const grouped = useMemo(() => {
    const dir = sortBy.endsWith("_asc") ? 1 : -1;
    const base = sortBy.replace(/_(asc|desc)$/, "");
    const agg = (lines) => lines.reduce((acc, l) => {
      acc.pending += l.pending_qty;
      acc.order += l.order_qty;
      acc.accepted += l.accepted_qty;
      acc.rework += l.rework_qty;
      return acc;
    }, { pending: 0, order: 0, accepted: 0, rework: 0 });
    const sortGroups = (groups) => [...groups].sort((a, b) => {
      const ga = agg(a.lines), gb = agg(b.lines);
      switch (base) {
        case "po": return dir * (a.po_number || "").localeCompare(b.po_number || "");
        case "product": return dir * (a.product_code || a.label || "").localeCompare(b.product_code || b.label || "");
        case "customer": return dir * (a.customer_name || "").localeCompare(b.customer_name || "");
        case "order": return dir * (ga.order - gb.order);
        case "accepted": return dir * (ga.accepted - gb.accepted);
        case "rework": return dir * (ga.rework - gb.rework);
        case "percent": {
          const pa = ga.order > 0 ? ga.accepted / ga.order : 0;
          const pb = gb.order > 0 ? gb.accepted / gb.order : 0;
          return dir * (pa - pb);
        }
        case "pending":
        default: return dir * (ga.pending - gb.pending);
      }
    });

    if (groupBy === "product") {
      const byProduct = {};
      filteredLines.forEach(ln => {
        const key = ln.product_code;
        (byProduct[key] ??= { key, label: `${ln.product_code} — ${ln.description}`, product_code: ln.product_code, customer_name: ln.customer_name, lines: [] }).lines.push(ln);
      });
      return sortGroups(Object.values(byProduct));
    }
    const byPo = {};
    filteredLines.forEach(ln => {
      (byPo[ln.po_id] ??= {
        key: ln.po_id,
        label: `${ln.po_number} — ${ln.customer_name || "Unassigned"}`,
        po_number: ln.po_number,
        customer_name: ln.customer_name,
        product_code: ln.product_code,
        dispatch_status: ln.dispatch_status,
        lines: [],
      }).lines.push(ln);
    });
    return sortGroups(Object.values(byPo));
  }, [filteredLines, groupBy, sortBy]);

  const overall = summary.overall || { ordered: 0, accepted: 0, rework: 0, pending: 0, line_complete: 0, line_total: 0, over_lines: 0 };
  const filteredSummary = useMemo(() => {
    return filteredLines.reduce((acc, ln) => {
      acc.pending += ln.pending_qty;
      acc.dispatched += (ln.dispatched_qty || 0);
      acc.lines += 1;
      if (ln.status === "complete") acc.complete += 1;
      if (ln.is_over) acc.over += 1;
      return acc;
    }, { pending: 0, dispatched: 0, lines: 0, complete: 0, over: 0 });
  }, [filteredLines]);

  const exportExcel = async () => {
    try {
      const qs = new URLSearchParams();
      if (selectedPoIds.length) qs.set("po_ids", selectedPoIds.join(","));
      qs.set("include_pending_approval", String(includePending));
      qs.set("dispatch_status", dispatchFilter);
      const res = await axios.get(`${API}/production/pending-summary/export?${qs.toString()}`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PendingProduction_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to export"); }
  };

  const StatusPill = ({ s }) => {
    const meta = STATUS_META[s] || STATUS_META.not_started;
    return <span className={`px-2 py-0.5 rounded border text-xs ${meta.cls}`}>{meta.label}</span>;
  };

  const toggleGroup = (k) => setExpandedGroups(prev => ({ ...prev, [k]: !prev[k] }));

  const toggleLine = async (poId, productCode) => {
    const key = `${poId}|${productCode}`;
    const willOpen = !expandedLines[key];
    setExpandedLines(prev => ({ ...prev, [key]: willOpen }));
    if (willOpen && !(key in lineReports)) {
      setLineReports(prev => ({ ...prev, [key]: null }));
      try {
        const res = await axios.get(`${API}/po/${poId}/line-item-reports/${encodeURIComponent(productCode)}`, auth());
        setLineReports(prev => ({ ...prev, [key]: res.data.reports || [] }));
      } catch {
        toast.error("Failed to load reports for this line");
        setLineReports(prev => ({ ...prev, [key]: [] }));
      }
    }
  };

  // Filter a ship-to group based on excluded POs:
  // - removes excluded POs from each material's by_po
  // - recomputes totals (order/accepted/pending) from remaining POs
  // - drops materials whose POs are all excluded
  const buildFilteredShiptoGroup = (group) => {
    const excludedSet = excludedPoByShipto[group.ship_to_canonical] || {};
    const includedPos = (group.pos || []).filter((p) => !excludedSet[p.po_id]);
    const materials = [];
    for (const m of (group.materials || [])) {
      const remainingPos = (m.by_po || []).filter((p) => !excludedSet[p.po_id]);
      if (remainingPos.length === 0) continue;
      const order_qty = remainingPos.reduce((s, p) => s + (p.order_qty || 0), 0);
      const accepted_qty = remainingPos.reduce((s, p) => s + (p.accepted_qty || 0), 0);
      const pending_qty = remainingPos.reduce((s, p) => s + (p.pending_qty || 0), 0);
      const percent = order_qty > 0 ? Math.min(100, Math.round((accepted_qty / order_qty) * 100)) : 0;
      materials.push({ ...m, by_po: remainingPos, order_qty, accepted_qty, pending_qty, percent });
    }
    const order_total = materials.reduce((s, m) => s + m.order_qty, 0);
    const accepted_total = materials.reduce((s, m) => s + m.accepted_qty, 0);
    const pending_total = materials.reduce((s, m) => s + m.pending_qty, 0);
    const percent = order_total > 0 ? Math.min(100, Math.round((accepted_total / order_total) * 100)) : 0;
    return { ...group, pos: includedPos, materials, po_count: includedPos.length, order_total, accepted_total, pending_total, percent };
  };

  const togglePoExclusion = (canonical, poId) => {
    setExcludedPoByShipto((prev) => {
      const next = { ...prev };
      const cur = { ...(next[canonical] || {}) };
      if (cur[poId]) delete cur[poId];
      else cur[poId] = true;
      next[canonical] = cur;
      return next;
    });
  };

  const setAllPosForShipto = (canonical, poIds, exclude) => {
    setExcludedPoByShipto((prev) => {
      const next = { ...prev };
      if (exclude) {
        const m = {};
        poIds.forEach((id) => { m[id] = true; });
        next[canonical] = m;
      } else {
        next[canonical] = {};
      }
      return next;
    });
  };

  const openPrintPreview = (group) => {
    const filtered = buildFilteredShiptoGroup(group);
    if (!filtered.materials.length) {
      toast.error("No materials to print – all POs are unchecked.");
      return;
    }
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocked – please allow pop-ups to preview."); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const showOrdered = qtyMode === "ordered" || qtyMode === "both";
    const showPending = qtyMode === "pending" || qtyMode === "both";
    const qtyHeaders = [
      showOrdered ? `<th class="num">Ordered Qty</th>` : "",
      showPending ? `<th class="num">Pending Qty</th>` : "",
    ].join("");
    const totalOrdered = filtered.order_total;
    const totalPending = filtered.pending_total;
    const rows = filtered.materials.map((m, i) => `
      <tr>
        <td class="ctr">${i + 1}</td>
        <td class="ctr mono">${esc(m.product_code)}</td>
        <td>${esc(m.description || "")}</td>
        ${showOrdered ? `<td class="num mono">${m.order_qty}</td>` : ""}
        ${showPending ? `<td class="num mono">${m.pending_qty}</td>` : ""}
        <td></td>
      </tr>
    `).join("");
    const poList = filtered.pos.map((p) => esc(p.po_number)).join(" · ") || "—";
    const consignees = (filtered.consignee_names || []).join(", ");
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Production Plan – ${esc(filtered.ship_to_display)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #18181b; margin: 24px; }
        h1 { text-align: center; font-size: 20px; margin: 0 0 4px; }
        .sub { text-align: center; font-size: 12px; color: #52525b; margin-bottom: 4px; }
        .meta { text-align: center; font-size: 11px; color: #71717a; margin-bottom: 14px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #18181b; padding: 6px 8px; vertical-align: middle; }
        th { background: #f4f4f5; text-align: left; }
        .num { text-align: right; }
        .ctr { text-align: center; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        tfoot td { font-weight: 600; background: #fafafa; }
        .totals { margin-top: 14px; font-size: 12px; }
        .sigs { margin-top: 36px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; font-size: 11px; }
        .sig { border-top: 1px solid #18181b; padding-top: 6px; text-align: center; }
        .toolbar { position: sticky; top: 0; background: white; padding: 8px 0 12px; border-bottom: 1px solid #e4e4e7; margin-bottom: 18px; display: flex; gap: 8px; justify-content: flex-end; }
        .btn { font: inherit; padding: 6px 14px; border: 1px solid #18181b; background: #18181b; color: white; cursor: pointer; border-radius: 4px; }
        .btn.secondary { background: white; color: #18181b; }
        @media print {
          .toolbar { display: none; }
          body { margin: 12mm; }
          @page { size: A4; margin: 12mm; }
        }
      </style></head><body>
      <div class="toolbar">
        <button class="btn secondary" onclick="window.close()">Close</button>
        <button class="btn" onclick="window.print()">Print</button>
      </div>
      <h1>${esc(filtered.ship_to_display || "Production Plan")}</h1>
      <div class="sub">${poList}</div>
      ${consignees ? `<div class="meta">Consignee: ${esc(consignees)}</div>` : ""}
      <table>
        <thead>
          <tr>
            <th class="ctr" style="width:36px;">Sr.</th>
            <th class="ctr" style="width:130px;">Material / Model No.</th>
            <th>Description</th>
            ${qtyHeaders}
            <th style="width:140px;">Remark</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td colspan="3" class="num">Total</td>
            ${showOrdered ? `<td class="num mono">${totalOrdered}</td>` : ""}
            ${showPending ? `<td class="num mono">${totalPending}</td>` : ""}
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div class="totals">
        Lines: <b>${filtered.materials.length}</b>
        &nbsp;·&nbsp; POs: <b>${filtered.po_count}</b>
        &nbsp;·&nbsp; Progress: <b>${filtered.percent}%</b>
      </div>
      <div class="sigs">
        <div class="sig">Prepared By</div>
        <div class="sig">Coating Supervisor</div>
        <div class="sig">Department Head</div>
      </div>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const startReportFromMaterial = (po, mat) => {
    // Per-PO buttons pass the by_po sub-row (with po.pending_qty for that specific PO);
    // the single-PO aggregated row passes by_po[0] which has the same value as mat.
    const qty = (po && po.pending_qty != null) ? po.pending_qty : mat.pending_qty;
    const params = new URLSearchParams({
      prefill_po: po.po_id,
      prefill_code: mat.product_code,
      prefill_qty: String(qty),
    });
    navigate(`/safety/production-reports/new?${params.toString()}`);
  };

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[
        { label: "Safety", to: "/safety" },
        { label: "Pending Production Tracker" },
      ]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Pending Production", active: true, depth: 1 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Production Reports", to: "/safety/production-reports", depth: 1 },
      ]}
    >
    <div data-testid="pending-production-tracker">
      <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-orange-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Pending Production Tracker</h1>
            <p className="text-xs text-zinc-500">Products awaiting coating across POs · group by PO, Product, or Ship-To Location</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={fetchSummary} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {aliasMismatchCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="alias-mismatch-banner">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                <b>{aliasMismatchCount}</b> product code{aliasMismatchCount === 1 ? "" : "s"} used in POs / reports {aliasMismatchCount === 1 ? "isn't" : "aren't"} linked to the catalog — produced or dispatched qty for {aliasMismatchCount === 1 ? "it" : "them"} may be understated here.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => navigate("/safety/catalog?aliasHealth=1")}
              data-testid="alias-mismatch-fix-btn"
            >
              Review Alias Health
            </Button>
          </div>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="POs in view" value={summary.pos?.length || 0} testId="stat-pos" />
          <SummaryCard label="Pending qty" value={overall.pending} highlight testId="stat-pending" />
          <SummaryCard label="Lines complete" value={`${overall.line_complete} / ${overall.line_total}`} testId="stat-lines" />
          <SummaryCard
            label="Over-produced lines"
            value={overall.over_lines}
            warn={overall.over_lines > 0}
            testId="stat-over"
            icon={overall.over_lines > 0 ? <AlertTriangle className="w-4 h-4" /> : null}
          />
        </div>

        {/* Filter bar */}
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
              <Input
                className="pl-8"
                placeholder="Search PO / product code / description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="tracker-search"
              />
            </div>
            <Button variant="outline" onClick={openPoPicker} data-testid="select-pos-btn">
              <ListChecks className="w-4 h-4 mr-1" />
              POs {selectedPoIds.length ? `(${selectedPoIds.length})` : "(All open)"}
            </Button>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-48" data-testid="customer-filter"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customerOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="not_started">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="over">Over-produced</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dispatchFilter} onValueChange={setDispatchFilter}>
              <SelectTrigger className="w-52" data-testid="dispatch-filter">
                <Truck className="w-3.5 h-3.5 mr-1 text-gray-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Dispatch: All (open POs)</SelectItem>
                <SelectItem value="fully">Dispatched (full)</SelectItem>
                <SelectItem value="partial">Partially Dispatched</SelectItem>
                <SelectItem value="none">Not Dispatched</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {dispatchFilter !== "all" && (
            <div className="text-xs text-gray-500 -mt-1" data-testid="dispatch-scope-note">
              Showing <b>all POs</b> (including completed) matching dispatch status:
              <span className={`ml-1 px-1.5 py-0.5 rounded border ${DISPATCH_META[dispatchFilter]?.cls}`}>{DISPATCH_META[dispatchFilter]?.label}</span>
            </div>
          )}
          <div className="flex items-center gap-6 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-gray-600">Counted reports:</span>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={includePending} onChange={() => setIncludePending(true)} data-testid="include-pending-yes" />
                <span>Approved + Pending Approval</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={!includePending} onChange={() => setIncludePending(false)} data-testid="include-pending-no" />
                <span>Approved only</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Group by:</span>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={groupBy === "po"} onChange={() => setGroupBy("po")} data-testid="groupby-po" />
                <span>PO</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={groupBy === "product"} onChange={() => setGroupBy("product")} data-testid="groupby-product" />
                <span>Product Code</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={groupBy === "shipto"} onChange={() => setGroupBy("shipto")} data-testid="groupby-shipto" />
                <span><MapPin className="inline w-3 h-3 mr-0.5 -mt-0.5" />Ship-To Location</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Sort:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 h-8" data-testid="sort-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_desc">Pending Qty (high → low)</SelectItem>
                  <SelectItem value="pending_asc">Pending Qty (low → high)</SelectItem>
                  <SelectItem value="po_asc">PO Number A-Z</SelectItem>
                  <SelectItem value="po_desc">PO Number Z-A</SelectItem>
                  <SelectItem value="product_asc">Product Code A-Z</SelectItem>
                  <SelectItem value="product_desc">Product Code Z-A</SelectItem>
                  <SelectItem value="customer_asc">Customer A-Z</SelectItem>
                  <SelectItem value="order_desc">Order Qty (high → low)</SelectItem>
                  <SelectItem value="order_asc">Order Qty (low → high)</SelectItem>
                  <SelectItem value="accepted_desc">Accepted (high → low)</SelectItem>
                  <SelectItem value="accepted_asc">Accepted (low → high)</SelectItem>
                  <SelectItem value="percent_asc">% Complete (low → high)</SelectItem>
                  <SelectItem value="percent_desc">% Complete (high → low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-xs text-gray-500">
              Showing <b>{filteredSummary.lines}</b> lines · <b>{filteredSummary.pending}</b> pending qty
              {filteredSummary.dispatched > 0 && <> · <b className="text-blue-600">{filteredSummary.dispatched}</b> dispatched</>}
              {filteredSummary.complete > 0 && <> · <b>{filteredSummary.complete}</b> complete</>}
              {filteredSummary.over > 0 && <> · <b className="text-purple-600">{filteredSummary.over}</b> over-produced</>}
            </div>
          </div>
        </div>

        {/* Grouped results */}
        {groupBy === "shipto" ? (
          shiptoLoading ? (
            <div className="flex items-center justify-center p-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
            </div>
          ) : !shiptoGroups || shiptoGroups.length === 0 ? (
            <div className="bg-white border rounded-lg p-10 text-center" data-testid="tracker-empty-state">
              <div className="text-4xl mb-2">✅</div>
              <p className="font-medium text-gray-700">No pending production by ship-to.</p>
              <p className="text-sm text-gray-500">All open POs are fully coated, or none have a ship-to set.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="shipto-groups">
              <div className="bg-white border rounded-lg px-4 py-2 flex items-center gap-3 text-sm flex-wrap" data-testid="shipto-qty-mode-bar">
                <span className="text-zinc-500">Show quantity:</span>
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={qtyMode === "pending"} onChange={() => setQtyMode("pending")} data-testid="qtymode-pending" />
                  <span>Pending Qty</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={qtyMode === "ordered"} onChange={() => setQtyMode("ordered")} data-testid="qtymode-ordered" />
                  <span>Ordered Qty</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" checked={qtyMode === "both"} onChange={() => setQtyMode("both")} data-testid="qtymode-both" />
                  <span>Both</span>
                </label>
                <span className="ml-auto text-xs text-zinc-500">Print mirrors this selection</span>
              </div>
              {shiptoGroups.map((g) => {
                const filtered = buildFilteredShiptoGroup(g);
                const isOpen = expandedGroups[`shipto:${g.ship_to_canonical}`] !== false;
                const urgencyDot = g.urgency === "red" ? "bg-red-500" : g.urgency === "amber" ? "bg-amber-500" : "bg-emerald-500";
                const cardBorder = g.urgency === "red" ? "border-l-red-500" : g.urgency === "amber" ? "border-l-amber-500" : "border-l-emerald-500";
                const excludedSet = excludedPoByShipto[g.ship_to_canonical] || {};
                const allPoIds = (g.pos || []).map((p) => p.po_id);
                const allChecked = allPoIds.every((id) => !excludedSet[id]);
                const noneChecked = allPoIds.every((id) => excludedSet[id]);
                const showOrdered = qtyMode === "ordered" || qtyMode === "both";
                const showPending = qtyMode === "pending" || qtyMode === "both";
                return (
                  <div key={g.ship_to_canonical} className={`bg-white border rounded-lg overflow-hidden border-l-4 ${cardBorder}`} data-testid={`shipto-group-${g.ship_to_canonical}`}>
                    <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap bg-zinc-50 border-b">
                      <button
                        className="flex items-center gap-2 text-left flex-1 min-w-0"
                        onClick={() => toggleGroup(`shipto:${g.ship_to_canonical}`)}
                        data-testid={`shipto-toggle-${g.ship_to_canonical}`}
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                        <MapPin className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className={`w-2 h-2 rounded-full ${urgencyDot} shrink-0`} title={`urgency: ${g.urgency}`} />
                        <span className="font-semibold truncate">{g.ship_to_display}</span>
                        {g.consignee_names?.length > 0 && (
                          <span className="text-xs text-zinc-500 truncate">· {g.consignee_names.join(", ")}</span>
                        )}
                        <span className="text-xs text-zinc-400 shrink-0">· {filtered.po_count}/{g.po_count} PO{g.po_count !== 1 ? "s" : ""}</span>
                        {g.min_required_date && (
                          <span className="text-xs text-zinc-500 shrink-0">· due {String(g.min_required_date).slice(0, 10)}</span>
                        )}
                      </button>
                      <div className="flex items-center gap-3 text-sm">
                        {showOrdered && <span className="text-zinc-500">Ordered: <b className="sf-mono">{filtered.order_total}</b></span>}
                        {showPending && <span className="text-zinc-500">Pending: <b className="text-orange-600 sf-mono">{filtered.pending_total}</b></span>}
                        <span className="text-zinc-500">Progress: <b className="sf-mono">{filtered.percent}%</b></span>
                        <Button onClick={() => openPrintPreview(g)} variant="outline" size="sm" data-testid={`shipto-print-${g.ship_to_canonical}`}>
                          <Printer className="w-3.5 h-3.5 mr-1" /> Print
                        </Button>
                      </div>
                    </div>
                    {isOpen && (
                      <div>
                        {/* PO checkbox list */}
                        <div className="px-4 py-2 border-b flex items-center gap-3 text-xs bg-zinc-50/40 flex-wrap">
                          <span className="text-zinc-500 font-medium">POs:</span>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={allChecked ? true : (noneChecked ? false : "indeterminate")}
                              onCheckedChange={(v) => setAllPosForShipto(g.ship_to_canonical, allPoIds, !v)}
                              data-testid={`shipto-po-selectall-${g.ship_to_canonical}`}
                            />
                            <span className="text-zinc-500">{allChecked ? "All" : noneChecked ? "None" : "Some"}</span>
                          </div>
                          <span className="text-zinc-300">|</span>
                          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                            {g.pos.map((p) => {
                              const checked = !excludedSet[p.po_id];
                              return (
                                <label key={p.po_id} className="inline-flex items-center gap-1.5 cursor-pointer">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => togglePoExclusion(g.ship_to_canonical, p.po_id)}
                                    data-testid={`shipto-po-cb-${p.po_number}`}
                                  />
                                  <button
                                    onClick={() => {
                                      pushRecent({ type: "po", id: p.po_id, label: `PO ${p.po_number}`, sublabel: p.customer_name || "", path: `/safety/po/${p.po_id}/detail` });
                                      navigate(`/safety/po/${p.po_id}/detail`);
                                    }}
                                    className={`sf-link-lateral inline-flex items-center gap-0.5 sf-mono ${checked ? "" : "line-through text-zinc-400"}`}
                                    data-testid={`shipto-po-chip-${p.po_number}`}
                                  >
                                    {p.po_number}
                                  </button>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        {/* Materials table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-white text-zinc-500 border-b">
                              <tr>
                                <th className="p-2 text-left w-10">Sr.</th>
                                <th className="p-2 text-left">Code</th>
                                <th className="p-2 text-left">Description</th>
                                {showOrdered && <th className="p-2 text-right">Ordered Qty</th>}
                                <th className="p-2 text-right">Coated</th>
                                {showPending && <th className="p-2 text-right">Pending Qty</th>}
                                <th className="p-2 text-right">%</th>
                                <th className="p-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.materials.length === 0 ? (
                                <tr><td colSpan={8} className="p-4 text-center text-zinc-500 text-xs italic">No materials – all POs are unchecked.</td></tr>
                              ) : filtered.materials.map((m, i) => {
                                const matKey = `${g.ship_to_canonical}|${m.product_code}`;
                                const matOpen = !!expandedShiptoMats[matKey];
                                return (
                                  <ShiptoMatRow
                                    key={matKey}
                                    idx={i}
                                    mat={m}
                                    showOrdered={showOrdered}
                                    showPending={showPending}
                                    open={matOpen}
                                    onToggle={() => setExpandedShiptoMats(prev => ({ ...prev, [matKey]: !prev[matKey] }))}
                                    onMarkCoated={(po) => startReportFromMaterial(po, m)}
                                    onOpenPo={(po) => {
                                      pushRecent({ type: "po", id: po.po_id, label: `PO ${po.po_number}`, sublabel: "", path: `/safety/po/${po.po_id}/detail?line=${encodeURIComponent(m.product_code)}` });
                                      navigate(`/safety/po/${po.po_id}/detail?line=${encodeURIComponent(m.product_code)}`);
                                    }}
                                  />
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : loading ? (
          <div className="flex items-center justify-center p-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="bg-white border rounded-lg p-10 text-center" data-testid="tracker-empty-state">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-medium text-gray-700">
              {dispatchFilter !== "all"
                ? `No POs are "${DISPATCH_META[dispatchFilter]?.label}".`
                : "No pending products match these filters."}
            </p>
            <p className="text-sm text-gray-500">
              {dispatchFilter !== "all"
                ? "Try a different dispatch status, or switch back to All (open POs)."
                : "Either all selected POs are fully coated, or filters excluded everything."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {grouped.map(g => {
              const totalPending = g.lines.reduce((s, l) => s + l.pending_qty, 0);
              const totalOrdered = g.lines.reduce((s, l) => s + l.order_qty, 0);
              const totalAccepted = g.lines.reduce((s, l) => s + l.accepted_qty, 0);
              const totalDispatched = g.lines.reduce((s, l) => s + (l.dispatched_qty || 0), 0);
              const totalOrderedWt = g.lines.reduce((s, l) => s + (l.ordered_weight_kg || 0), 0);
              const totalToDispatchWt = g.lines.reduce((s, l) => s + (l.to_dispatch_weight_kg || 0), 0);
              const someUnweighed = g.lines.some((l) => l.order_qty > 0 && l.ordered_weight_kg == null);
              const isOpen = expandedGroups[g.key] !== false;
              const dmeta = DISPATCH_META[g.dispatch_status] || DISPATCH_META.none;
              return (
                <div key={g.key} className="bg-white border rounded-lg overflow-hidden" data-testid={`group-${g.key}`}>
                  <button
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 border-b"
                    onClick={() => toggleGroup(g.key)}
                    data-testid={`group-toggle-${g.key}`}
                  >
                    <div className="flex items-center gap-2 text-left">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-semibold">{g.label}</span>
                      <span className="text-xs text-gray-500">· {g.lines.length} line{g.lines.length === 1 ? "" : "s"}</span>
                      {groupBy === "po" && (
                        <span className={`px-2 py-0.5 rounded border text-xs ${dmeta.cls}`} data-testid={`group-dispatch-badge-${g.key}`}>
                          <Truck className="inline w-3 h-3 mr-0.5 -mt-0.5" />{dmeta.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-600">Ordered: <b>{totalOrdered}</b></span>
                      <span className="text-gray-600">Accepted: <b>{totalAccepted}</b></span>
                      <span className="text-emerald-700">Pending: <b>{totalPending}</b></span>
                      <span className="text-blue-700">Dispatched: <b>{totalDispatched}</b></span>
                      <span className="inline-flex items-center gap-1 text-violet-700 border-l border-zinc-200 pl-3" title="Net weight of the full order (for freight planning). 'to ship' = weight still to dispatch." data-testid={`group-weight-${g.key}`}>
                        <Scale className="w-3.5 h-3.5" />
                        <b>{totalOrderedWt.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</b>
                        {totalToDispatchWt > 0 && totalToDispatchWt !== totalOrderedWt && (
                          <span className="text-xs text-violet-500">· {totalToDispatchWt.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg to ship</span>
                        )}
                        {someUnweighed && <span className="text-[10px] text-zinc-400">·*</span>}
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-white text-gray-600 border-b">
                          <tr>
                            {groupBy === "product" && <SortableHeader label="PO No" col="po" sortBy={sortBy} setSortBy={setSortBy} align="left" />}
                            {groupBy === "product" && <SortableHeader label="Customer" col="customer" sortBy={sortBy} setSortBy={setSortBy} align="left" />}
                            {groupBy === "po" && <SortableHeader label="Product Code" col="product" sortBy={sortBy} setSortBy={setSortBy} align="left" />}
                            {groupBy === "po" && <SortableHeader label="Description" col="description" sortBy={sortBy} setSortBy={setSortBy} align="left" />}
                            <SortableHeader label="Order Qty" col="order" sortBy={sortBy} setSortBy={setSortBy} align="right" />
                            <SortableHeader label="Accepted" col="accepted" sortBy={sortBy} setSortBy={setSortBy} align="right" />
                            <SortableHeader label="Rework" col="rework" sortBy={sortBy} setSortBy={setSortBy} align="right" />
                            <SortableHeader label="Pending" col="pending" sortBy={sortBy} setSortBy={setSortBy} align="right" />
                            <th className="p-2 text-right text-blue-700">Dispatched</th>
                            <th className="p-2 text-right text-blue-700">To Dispatch</th>
                            <SortableHeader label="%" col="percent" sortBy={sortBy} setSortBy={setSortBy} align="right" widthClass="w-32" />
                            <SortableHeader label="Status" col="status" sortBy={sortBy} setSortBy={setSortBy} align="left" />
                          </tr>
                        </thead>
                        <tbody>
                          {g.lines.map((ln, i) => {
                            const lineKey = `${ln.po_id}|${ln.product_code}`;
                            const lineOpen = !!expandedLines[lineKey];
                            const reports = lineReports[lineKey];
                            const drill = () => {
                              pushRecent({
                                type: "po",
                                id: ln.po_id,
                                label: `PO ${ln.po_number}`,
                                sublabel: ln.customer_name || "",
                                path: `/safety/po/${ln.po_id}/detail?line=${encodeURIComponent(ln.product_code)}`,
                              });
                              navigate(`/safety/po/${ln.po_id}/detail?line=${encodeURIComponent(ln.product_code)}`);
                            };
                            const Chevron = (
                              <button
                                onClick={() => toggleLine(ln.po_id, ln.product_code)}
                                className="text-zinc-400 hover:text-zinc-700 mr-1 align-middle"
                                title={lineOpen ? "Hide reports" : "Show reports"}
                                data-testid={`line-toggle-${ln.po_id}-${ln.product_code}`}
                              >
                                {lineOpen ? <ChevronDown className="w-3 h-3 inline" /> : <ChevronRight className="w-3 h-3 inline" />}
                              </button>
                            );
                            const colSpan = (groupBy === "product" ? 2 : 2) + 7; // 2 leading + Order/Accepted/Rework/Pending/Dispatched/ToDispatch/% + Status = total 10
                            return (
                            <Fragment key={`${ln.po_id}-${ln.product_code}-${i}`}>
                            <tr className="border-b last:border-b-0 hover:bg-emerald-50/30" data-testid={`line-${ln.po_id}-${ln.product_code}`}>
                              {groupBy === "product" && (
                                <td className="p-2 font-medium">
                                  {Chevron}
                                  <button
                                    onClick={drill}
                                    className="sf-link-lateral inline-flex items-center gap-0.5 sf-mono"
                                    data-testid={`line-po-link-${ln.po_id}-${ln.product_code}`}
                                  >
                                    {ln.po_number}
                                  </button>
                                </td>
                              )}
                              {groupBy === "product" && <td className="p-2 text-gray-600">{ln.customer_name}</td>}
                              {groupBy === "po" && (
                                <td className="p-2 font-medium">
                                  {Chevron}
                                  <button
                                    onClick={drill}
                                    className="sf-link-lateral inline-flex items-center gap-0.5 sf-mono"
                                    data-testid={`line-product-link-${ln.po_id}-${ln.product_code}`}
                                  >
                                    {ln.product_code}
                                  </button>
                                </td>
                              )}
                              {groupBy === "po" && <td className="p-2 text-gray-600">{ln.description}</td>}
                              <td className="p-2 text-right">{ln.order_qty}</td>
                              <td className="p-2 text-right">{ln.accepted_qty}</td>
                              <td className="p-2 text-right text-gray-500">{ln.rework_qty}</td>
                              <td className={`p-2 text-right font-semibold ${ln.pending_qty > 0 ? "text-emerald-700" : "text-gray-400"}`}>
                                <div className="flex flex-col items-end gap-0.5">
                                  <span>{ln.pending_qty}</span>
                                  {ln.awaiting_dpr_qty > 0 && (
                                    <span
                                      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 whitespace-nowrap"
                                      title={`${ln.awaiting_dpr_qty} pc(s) already on pallets awaiting DPR${ln.fully_palletized ? " — fully palletized, so this line is not offered in the scanner PO picker for new pallets" : ""}.`}
                                      data-testid={`line-onpallet-${ln.po_id}-${ln.product_code}`}
                                    >
                                      {ln.fully_palletized ? "fully palletized" : `${ln.awaiting_dpr_qty} on pallets`}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className={`p-2 text-right font-medium ${ln.dispatched_qty > 0 ? "text-blue-700" : "text-gray-400"}`} data-testid={`line-dispatched-${ln.po_id}-${ln.product_code}`}>{ln.dispatched_qty || 0}</td>
                              <td className={`p-2 text-right ${ln.to_dispatch_qty > 0 ? "text-gray-700" : "text-gray-400"}`}>{ln.to_dispatch_qty ?? ln.order_qty}</td>
                              <td className="p-2 text-right">
                                <div className="flex items-center gap-2 justify-end">
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full ${ln.is_over ? "bg-purple-500" : ln.status === "complete" ? "bg-emerald-500" : ln.accepted_qty > 0 ? "bg-amber-500" : "bg-gray-300"}`}
                                      style={{ width: `${Math.min(100, ln.percent || 0)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-600 font-mono w-9 text-right">{ln.percent}%</span>
                                </div>
                              </td>
                              <td className="p-2"><StatusPill s={ln.status} /></td>
                            </tr>
                            {lineOpen && (
                              <tr className="bg-zinc-50/60 border-b last:border-b-0" data-testid={`line-reports-${ln.po_id}-${ln.product_code}`}>
                                <td colSpan={colSpan} className="p-3 pl-8">
                                  <LineItemReportsPanel
                                    poNumber={ln.po_number}
                                    productCode={ln.product_code}
                                    reports={reports}
                                    onOpen={(rid) => {
                                      pushRecent({
                                        type: "report", id: rid, label: `Report`,
                                        sublabel: `${ln.po_number} · ${ln.product_code}`,
                                        path: `/safety/production-reports/${rid}`,
                                      });
                                      navigate(`/safety/production-reports/${rid}`);
                                    }}
                                  />
                                </td>
                              </tr>
                            )}
                            </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PO picker */}
      <Dialog open={poPickerOpen} onOpenChange={setPoPickerOpen}>
        <DialogContent className="max-w-4xl" data-testid="tracker-po-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select Purchase Orders</DialogTitle>
            <DialogDescription>Pick POs to include. Leave empty + "Select All" to view every open PO.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input placeholder="Search PO / Customer / Ship To" value={poSearch} onChange={(e) => setPoSearch(e.target.value)} data-testid="picker-po-search" />
            <Button variant="outline" onClick={selectAllOpenPos} data-testid="select-all-open-pos">Select All Open</Button>
            <Button variant="outline" onClick={clearPickerSelection} data-testid="clear-po-selection">Clear</Button>
          </div>
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 w-10"></th>
                  <th className="p-2 text-left">PO No</th>
                  <th className="p-2 text-left">Customer</th>
                  <th className="p-2 text-left">Ship To</th>
                  <th className="p-2 text-right">Products</th>
                  <th className="p-2 text-right">Order Qty</th>
                  <th className="p-2 text-right">Pending Qty</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOpenPos.length === 0 && <tr><td colSpan="8" className="p-4 text-center text-gray-500">No open POs found.</td></tr>}
                {filteredOpenPos.map(p => (
                  <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`picker-po-${p.id}`}>
                    <td className="p-2">
                      <Checkbox checked={!!poPickerSelected[p.id]} onCheckedChange={(v) => setPoPickerSelected({ ...poPickerSelected, [p.id]: !!v })} />
                    </td>
                    <td className="p-2 font-medium">{p.po_number}</td>
                    <td className="p-2">{p.customer_name || <span className="text-amber-600 text-xs">Not assigned</span>}</td>
                    <td className="p-2 text-gray-600">{p.ship_to_name || "-"}</td>
                    <td className="p-2 text-right">{p.product_count}</td>
                    <td className="p-2 text-right">{p.total_ordered ?? "-"}</td>
                    <td className="p-2 text-right">{p.total_pending}</td>
                    <td className="p-2">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoPickerOpen(false)}>Cancel</Button>
            <Button onClick={confirmPoPicker} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-po-picker">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}

function ShiptoMatRow({ idx, mat, open, onToggle, onMarkCoated, onOpenPo, showOrdered = true, showPending = true }) {
  // Total cols in outer table: Sr, Code, Desc, [Ordered], Coated, [Pending], %, action = 5 + showOrdered + showPending
  const innerColSpan = 4 + (showOrdered ? 1 : 0) + (showPending ? 1 : 0);
  return (
    <>
      <tr className={`border-b last:border-b-0 ${open ? "bg-zinc-50/40" : "hover:bg-zinc-50/60"}`} data-testid={`shipto-mat-row-${mat.product_code}`}>
        <td className="p-2 text-zinc-400 text-center">{idx + 1}</td>
        <td className="p-2">
          <button onClick={onToggle} className="inline-flex items-center gap-1 sf-mono font-medium hover:text-orange-600" data-testid={`shipto-mat-toggle-${mat.product_code}`}>
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {mat.product_code}
          </button>
        </td>
        <td className="p-2 text-zinc-600 truncate max-w-[300px]" title={mat.description}>{mat.description}</td>
        {showOrdered && <td className="p-2 text-right sf-mono">{mat.order_qty}</td>}
        <td className="p-2 text-right sf-mono">{mat.accepted_qty}</td>
        {showPending && <td className="p-2 text-right sf-mono font-semibold" style={{ color: "var(--sf-accent)" }}>{mat.pending_qty}</td>}
        <td className="p-2 text-right sf-mono">{mat.percent}%</td>
        <td className="p-2 text-right">
          {mat.by_po.length === 1 && mat.pending_qty > 0 && (
            <Button onClick={() => onMarkCoated(mat.by_po[0])} variant="outline" size="sm" className="h-7 text-xs" data-testid={`shipto-mat-coat-${mat.product_code}`}>
              <Plus className="w-3 h-3 mr-1" /> Coat
            </Button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-white border-b last:border-b-0">
          <td></td>
          <td colSpan={innerColSpan} className="p-2 pl-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5"><Package className="inline w-3 h-3 mr-1" />Break-down by PO</div>
            <table className="w-full text-xs">
              <thead className="text-zinc-500">
                <tr className="text-left">
                  <th className="py-1">PO</th>
                  <th className="py-1 text-right">Order</th>
                  <th className="py-1 text-right">Coated</th>
                  <th className="py-1 text-right">Pending</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {mat.by_po.map((p) => (
                  <tr key={p.po_id} className="border-t border-zinc-100" data-testid={`shipto-mat-po-${mat.product_code}-${p.po_number}`}>
                    <td className="py-1.5">
                      <button onClick={() => onOpenPo(p)} className="sf-link-lateral inline-flex items-center gap-1 sf-mono">
                        {p.po_number}
                      </button>
                    </td>
                    <td className="py-1.5 text-right sf-mono">{p.order_qty}</td>
                    <td className="py-1.5 text-right sf-mono">{p.accepted_qty}</td>
                    <td className="py-1.5 text-right sf-mono" style={{ color: "var(--sf-accent)" }}>{p.pending_qty}</td>
                    <td className="py-1.5 text-right">
                      {p.pending_qty > 0 && (
                        <Button onClick={() => onMarkCoated(p)} variant="outline" size="sm" className="h-6 text-[10px]" data-testid={`shipto-mat-po-coat-${mat.product_code}-${p.po_number}`}>
                          <Plus className="w-2.5 h-2.5 mr-0.5" /> Coat
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

const REPORT_STATUS_PILL = {
  draft: "bg-gray-100 text-gray-700",
  pending_approval: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

function LineItemReportsPanel({ poNumber, productCode, reports, onOpen }) {
  if (reports === null || reports === undefined) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 py-2">
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600" />
        Loading reports for {poNumber} / {productCode}…
      </div>
    );
  }
  if (reports.length === 0) {
    return (
      <div className="text-xs text-zinc-500 italic py-1.5">
        No production reports yet for {poNumber} / {productCode}.
      </div>
    );
  }
  const total = reports.reduce((s, r) => s + (r.accepted_qty || 0), 0);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1.5">
        <FileText className="w-3 h-3" />
        Production reports — {poNumber} / {productCode}
        <span className="text-zinc-300">·</span>
        <span className="normal-case tracking-normal">{reports.length} report{reports.length === 1 ? "" : "s"}</span>
      </div>
      <table className="w-full text-xs">
        <thead className="text-zinc-500">
          <tr className="text-left">
            <th className="py-1 pr-2">Date</th>
            <th className="py-1 pr-2">Status</th>
            <th className="py-1 pr-2 text-right">Coated</th>
            <th className="py-1 pr-2 text-right">Rework</th>
            <th className="py-1 pr-2 text-right">Accepted</th>
            <th className="py-1 pr-2">Created by</th>
            <th className="py-1"></th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.report_id} className="border-t border-zinc-200" data-testid={`line-report-row-${r.report_id}`}>
              <td className="py-1.5 pr-2 sf-mono">{r.report_date}</td>
              <td className="py-1.5 pr-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${REPORT_STATUS_PILL[r.status] || REPORT_STATUS_PILL.draft}`}>
                  {r.status}
                </span>
              </td>
              <td className="py-1.5 pr-2 text-right sf-mono">{r.coated_qty}</td>
              <td className="py-1.5 pr-2 text-right sf-mono text-zinc-500">{r.rework_qty}</td>
              <td className="py-1.5 pr-2 text-right sf-mono font-semibold">{r.accepted_qty}</td>
              <td className="py-1.5 pr-2 text-zinc-600 truncate max-w-[150px]">{r.created_by_name || "—"}</td>
              <td className="py-1.5 text-right">
                <button
                  onClick={() => onOpen(r.report_id)}
                  className="sf-link-lateral inline-flex items-center gap-0.5"
                  data-testid={`line-report-open-${r.report_id}`}
                  title="Open report"
                >
                  <ArrowUpRight className="w-3 h-3" /> Open
                </button>
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-zinc-300">
            <td colSpan="4" className="py-1 pr-2 text-right text-zinc-500">Total accepted</td>
            <td className="py-1 pr-2 text-right sf-mono font-semibold">{total}</td>
            <td colSpan="2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({ label, col, sortBy, setSortBy, align = "left", widthClass = "" }) {
  const isAsc = sortBy === `${col}_asc`;
  const isDesc = sortBy === `${col}_desc`;
  const active = isAsc || isDesc;
  const onClick = () => {
    if (isAsc) setSortBy(`${col}_desc`);
    else if (isDesc) setSortBy(`${col}_asc`);
    else setSortBy(`${col}_desc`);
  };
  const Icon = isAsc ? ArrowUp : isDesc ? ArrowDown : ArrowUpDown;
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`p-2 text-${align} ${widthClass}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 ${justify} w-full cursor-pointer select-none hover:text-emerald-700 transition-colors ${active ? "text-emerald-700 font-semibold" : ""}`}
        data-testid={`sort-header-${col}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}

function SummaryCard({ label, value, highlight, warn, icon, testId }) {
  const base = "bg-white border rounded-lg p-4 flex flex-col gap-1";
  const tone = warn ? "border-purple-300 bg-purple-50" : highlight ? "border-emerald-300 bg-emerald-50" : "";
  return (
    <div className={`${base} ${tone}`} data-testid={testId}>
      <div className="text-xs text-gray-500 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-2xl font-bold ${warn ? "text-purple-700" : highlight ? "text-emerald-700" : "text-gray-800"}`}>
        {value}
      </div>
    </div>
  );
}
